import React, { useState, useMemo, useCallback } from 'react'
import { Box, styled, Typography } from '@mui/material'
import { EmptyState, Icon, Spinner, theme, Button } from '@ketch-com/deck'
import { DataCategoriesSharingItem } from 'pages/dataSentry/data/dataTabComponents/DataCategoriesSharingItem'
import { MorePlatformsModal } from 'pages/dataSentry/data/dataTabComponents/MorePlatformsModal'
import { useDebounce } from 'react-use'
import { ClassificationType } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { useDataSentryBySystemsClassificationType } from 'api/dataSentry/queries/useDataSentryBySystemsClassificationType'
import { useDataSentryContext } from 'pages/dataSentry/context/DataSentryContext'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'

const Container = styled(Box)({
  padding: '16px',
  backgroundColor: theme.palette.Common.Container,
  borderRadius: '11px',
  width: '100%',
})

const Header = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
})

const GridContainer = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '16px',
})

const ShowAllButton = styled(Button)({
  marginTop: '16px',
  textTransform: 'none',
})

export interface DataSystemInfo {
  count: number
  dataSystem: {
    code: string
    logoUrl: string
    name: string
  }
}

export const DataCategoriesSharing: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  const { selectedTimeRange, selectedPropertyCode } = useDataSentryContext()

  const { data, isLoading } = useDataSentryBySystemsClassificationType({
    params: {
      'classification-type': ClassificationType.DATA_CATEGORY,
      'start-date': selectedTimeRange?.startDate,
      'end-date': selectedTimeRange?.endDate,
      'property-codes': selectedPropertyCode,
    },
  })

  const [showAllCategories, setShowAllCategories] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<{ name: string; platforms: DataSystemInfo[] } | null>(null)

  const classifications = useMemo(() => data?.classifications || [], [data])
  const filteredClassifications = useMemo(() => {
    let result = debouncedSearchQuery
      ? classifications.filter(item => {
          const categoryName = item?.classification?.name?.toLowerCase()
          const query = debouncedSearchQuery.toLowerCase()
          return categoryName?.includes(query)
        })
      : [...classifications]

    return result.sort((a, b) => (b.dataSystems?.length || 0) - (a.dataSystems?.length || 0))
  }, [classifications, debouncedSearchQuery])

  const displayedClassifications = useMemo(() => {
    return showAllCategories ? filteredClassifications : filteredClassifications.slice(0, 9)
  }, [filteredClassifications, showAllCategories])

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery)
    },
    300,
    [searchQuery],
  )

  const handleShowAll = useCallback(() => {
    setShowAllCategories(true)
  }, [])

  const handleShowLess = useCallback(() => {
    setShowAllCategories(false)
  }, [])

  const displayDataEmptyState = Boolean(!searchQuery && !debouncedSearchQuery && !displayedClassifications.length)
  const displayFilterDataEmptyState = Boolean((searchQuery || debouncedSearchQuery) && !displayedClassifications.length)
  const displayData = Boolean(searchQuery || debouncedSearchQuery || displayedClassifications.length)

  return (
    <Container>
      <Header>
        <Typography pb={1} pl={0.5} variant="h4">
          Data Categories Sharing
        </Typography>

        {displayData && (
          <SearchTextInput
            onChange={nextValue => {
              setSearchQuery(nextValue)
            }}
            value={searchQuery}
            showClearButton
            minWidth="176px"
            sx={{
              width: '176px',
              '& .MuiInputBase-input': { maxWidth: '112px', minWidth: '112px' },
            }}
          />
        )}
      </Header>

      {isLoading && !displayData ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <Spinner size="32px" thickness={2.5} />
        </Box>
      ) : null}

      {!isLoading && displayDataEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px">
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            iconName="FCardsView"
            title="No Data"
            subTitle="Review your settings and connections, or wait for the scan to finish processing."
          />
        </Box>
      ) : null}

      {!isLoading && displayFilterDataEmptyState ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px">
          <EmptyState
            size="large"
            colorTheme={theme.palette.Text.Primary}
            iconName="OFilter"
            title="No results found"
            subTitle="Sorry, we couldn't find any results that match your filter criteria. Please adjust your filters and try again."
          />
        </Box>
      ) : null}

      {displayData ? (
        <>
          <GridContainer>
            {displayedClassifications.map(classification => (
              <DataCategoriesSharingItem key={classification?.classification?.code} classification={classification} />
            ))}
          </GridContainer>

          {filteredClassifications.length > 9 && (
            <Box display="flex" justifyContent="center">
              {!showAllCategories ? (
                <ShowAllButton
                  color="secondary"
                  variant="link"
                  size="large"
                  onClick={handleShowAll}
                  endIcon={<Icon name="OArrowDown" />}
                >
                  Show All ({filteredClassifications.length - 9} more)
                </ShowAllButton>
              ) : (
                <ShowAllButton
                  color="secondary"
                  variant="link"
                  size="large"
                  onClick={handleShowLess}
                  endIcon={<Icon name="OArrowUp" />}
                >
                  Show Less
                </ShowAllButton>
              )}
            </Box>
          )}
        </>
      ) : null}

      {selectedCategory && (
        <MorePlatformsModal
          isOpen={!!selectedCategory}
          onClose={() => setSelectedCategory(null)}
          categoryName={selectedCategory.name}
          platforms={selectedCategory.platforms}
        />
      )}
    </Container>
  )
}
